import SideModal from "components/SideModal";
import { useToast } from "contexts/ToastContext";
import { useTranslation } from "contexts/i18nContext";
import React, { ChangeEvent, useRef, useState } from "react";
import { editAccusedSummon } from "services/advocateComplaintService";
import { AccusedSummon, AdvocateComplaints } from "types";
import { LoadingIcon } from "utils/constant";
import { ADVOCATE_ACCUSED_COMPLAINT_STATUS } from "utils/constantValues";

interface ModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  summonData: AccusedSummon;
  complaintData: AdvocateComplaints;
  onRefresh: any;
}

export default function AddSummonResponse({
  isOpen,
  setIsOpen,
  summonData,
  complaintData,
  onRefresh,
}: ModalProps) {
  const { showToast } = useToast();
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState<AccusedSummon>({
    response_summary: "",
    files: [] as File[],
  });

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFormState((prev) => ({
        ...prev,
        files: [...Array.from(e.target.files as FileList)],
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    if (!summonData) {
      showToast(t("accusedSummonNotFound"), "error");
      return;
    }

    const isAccusedConfirmed = true;
    const isCoordinatorConfirmed = true;

    const data = new FormData();
    data.append("response", (formState?.files ?? [])[0] || "");
    data.append("accused_confirmed", isAccusedConfirmed.toString());
    data.append("coordinator_confirmed", isCoordinatorConfirmed.toString());

    data.append(
      "status",
      ADVOCATE_ACCUSED_COMPLAINT_STATUS.ASSIGNED_TO_PROSECUTOR,
    );
    data.append("response_summary", formState?.response_summary ?? "");
    data.append("admin_unit_id", complaintData?.admin_unit_id || "");
    data.append("org_id", complaintData?.org_id || "");
    data.append("accused_advocate_name", complaintData?.accused_name || "");
    data.append(
      "user_id_accused_advocate",
      complaintData?.userid_accused_name || "",
    );
    data.append(
      "accused_license_number",
      complaintData?.accused_license_number || "",
    );

    const summon_id = summonData?.id || "";
    const isFormData = true;

    try {
      await editAccusedSummon(data, summon_id, isFormData);

      onRefresh();
      setIsOpen(false);
      showToast(t("successfullyCreated"), "success");
      setFormState({
        response_summary: "",
        files: [] as File[],
      });
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("submissionError"), "error");
    } finally {
      setLoading(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  if (!isOpen) return null;

  return (
    <SideModal isOpen={isOpen} setIsOpen={setIsOpen} title={t("addResponse")}>
      <form onSubmit={handleSubmit} className="victim-form">
        <section className="evidence-section">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("responseSummary")}
            </label>
            <textarea
              required
              rows={3}
              name="response_summary"
              value={formState.response_summary ?? ""}
              onChange={handleChange}
              placeholder={t("detailsPlaceholder")}
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
            />
          </div>
        </section>

        <section className="evidence-section">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("responseFile")}
          </label>
          <div className="file-upload-area" onClick={triggerFileInput}>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              // multiple
              style={{ display: "none" }}
            />
            <p className="upload-text">{t("uploadFiles")}</p>
            {formState?.files && formState?.files?.length > 0 && (
              <div className="file-list">
                {formState?.files?.map((file, index) => (
                  <div key={index} className="file-item">
                    {file.name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <button
          type="submit"
          className="bg-black w-full text-white px-6 py-2 rounded-md hover:bg-gray-800"
          disabled={loading}
        >
          {loading ? (
            <div className="flex items-center justify-center">
              {LoadingIcon}
              {t("submitting")}
            </div>
          ) : (
            t("submit")
          )}
        </button>
      </form>
    </SideModal>
  );
}
